'use client';

import { CircularSpinnerß, Tagß } from '@otter-ai/den-components';
import type { GetWorkspaceResponse } from '@otter-ai/recall-client';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { PATH_URL } from '@/constants/path-constants';
import { useExperimentInGroup } from '@/experiment/experiments-client';
import { ContentContainer } from '@/features/account-settings/shared/components/ContentContainer';
import { useSubscription } from '@/features/account-settings/shared/hooks/useSubscription';
import { useManageTeamPageAccess } from '@/features/manage-team/components/useManageTeamPageAccess';
import { MembersTableHeader } from '@/features/manage-team/team-profile/MembersTableHeader';
import { TeamMembersTable } from '@/features/manage-team/team-profile/TeamMembersTable';
import { useTeamMembersActions } from '@/features/manage-team/team-profile/useTeamMembersActions';
import { useGetCurrentUser } from '@/shared/hooks/current-user/useGetCurrentUser';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import { OtterPlanType } from '@/shared/types/plan';
import { isEduAccount } from '@/shared/utils/subscriptionUtils';

const FILTERS = {
  MEMBERS: 'members',
  REQUESTED: 'requested',
  TRIALS: 'trials',
} as const;

type FilterValue = (typeof FILTERS)[keyof typeof FILTERS];

export default function TeamProfilePage() {
  const t = useTranslations('manage-team:team-profile');
  const tCommon = useTranslations('common');
  const searchParams = useSearchParams();
  const { workspace, adminStatus, isLoading, hasWorkspace } = useWorkspace();
  const { user } = useGetCurrentUser();
  const { subscription } = useSubscription();
  const enableWorkspaceHideAction = useExperimentInGroup(
    'enable_workspace_hide_action',
    'enabled',
  );
  const { hasAccess, isLoading: isAccessLoading } = useManageTeamPageAccess(
    PATH_URL.TEAM_PROFILE,
  );
  const [filter, setFilter] = useState<FilterValue>(FILTERS.MEMBERS);

  const ownerEmail = workspace?.owner?.email ?? null;
  const currentUserEmail = user?.email ?? null;
  const isCurrentUserAdmin = Boolean(adminStatus?.isAdmin);
  const isCurrentUserOwner = Boolean(
    ownerEmail &&
      currentUserEmail &&
      ownerEmail.toLowerCase() === currentUserEmail.toLowerCase(),
  );

  const memberActions = useTeamMembersActions({
    workspaceId: workspace?.id ?? 0,
    workspaceName: workspace?.name ?? '',
    workspaceDomains: workspace?.domains?.map((d) => d.domain) ?? [],
    inviteWhenSharing: workspace?.settings?.invite_when_sharing ?? false,
    requestToJoinClaimedDomainWorkspace:
      (
        workspace?.settings as {
          request_to_join_claimed_domain_workspace?: boolean;
        }
      )?.request_to_join_claimed_domain_workspace ?? false,
    salesAssisted: workspace?.settings?.sales_assisted ?? false,
    isEdu: subscription ? isEduAccount(subscription) : false,
  });

  // Hide members feature gate: enterprise OR edu OR experiment
  const isEnterprise = subscription?.plan_type === OtterPlanType.ENTERPRISE;
  const isEdu = subscription ? isEduAccount(subscription) : false;
  const canHideMembers = isEnterprise || isEdu || enableWorkspaceHideAction;

  const enableBulkManagement =
    (workspace?.settings?.bulk_management_enabled || isEnterprise) &&
    isCurrentUserAdmin &&
    filter === FILTERS.MEMBERS;

  // Basic workspace = user is in workspace AND subscription is on FREE plan
  const isBasicWorkspace = Boolean(
    hasWorkspace && subscription?.plan_type === OtterPlanType.FREE,
  );

  // Analytics tab access: admin + (Team/Enterprise/FreeTeam)
  const canAccessAnalytics =
    isCurrentUserAdmin &&
    (subscription?.plan_type === OtterPlanType.TEAM ||
      subscription?.plan_type === OtterPlanType.FREE_TEAM ||
      subscription?.plan_type === OtterPlanType.ENTERPRISE);

  const requestedCount = useMemo(() => {
    return (
      workspace?.pending_members?.filter(
        (memb) => memb.member_status === 'requested to join',
      ).length ?? 0
    );
  }, [workspace?.pending_members]);

  const trialsCount = useMemo(() => {
    return (
      workspace?.members?.filter((memb) => memb?.license_status === 'trial')
        .length ?? 0
    );
  }, [workspace?.members]);

  // Count trials ending soon (within 10 days)
  const trialsEndingSoonCount = useMemo(() => {
    if (!workspace?.members) return 0;
    const now = Date.now();
    const TEN_DAYS_MS = 10 * 24 * 60 * 60 * 1000;

    return workspace.members.filter((memb) => {
      if (memb?.license_status !== 'trial') return false;
      if (!memb.true_up_date) return false;

      const trueUpDate = new Date(memb.true_up_date as number).getTime();
      const timeUntilBilling = trueUpDate - now;

      return timeUntilBilling > 0 && timeUntilBilling < TEN_DAYS_MS;
    }).length;
  }, [workspace?.members]);

  // Tab visibility logic aligned with Angular implementation
  const allowTeamExpansionSettings =
    workspace?.allow_team_expansion_settings ?? false;
  const hasRequestDiscoverability =
    workspace?.settings?.discoverability === 'request';

  const showRequestedTab =
    isCurrentUserAdmin &&
    ((allowTeamExpansionSettings && hasRequestDiscoverability) ||
      requestedCount > 0);

  const showTrialsTab =
    (allowTeamExpansionSettings || trialsCount > 0) && !isBasicWorkspace;

  // Initialize filter from query parameter (only if user has access)
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam === FILTERS.MEMBERS) {
      setFilter(FILTERS.MEMBERS);
    } else if (filterParam === FILTERS.REQUESTED) {
      // Only allow access to requested tab if user has permission
      setFilter(showRequestedTab ? FILTERS.REQUESTED : FILTERS.MEMBERS);
    } else if (filterParam === FILTERS.TRIALS) {
      // Only allow access to trials tab if user has permission
      setFilter(showTrialsTab ? FILTERS.TRIALS : FILTERS.MEMBERS);
    }
  }, [searchParams, showRequestedTab, showTrialsTab]);

  const members = useMemo(() => {
    if (!workspace?.members) {
      return [];
    }
    if (filter === FILTERS.MEMBERS) {
      const activeMembers = workspace.members.filter(
        (m) => m.license_status !== 'trial',
      );
      // Only show invited/pending members to admins and owners (matches Angular: invited are in default_members but status/action columns are admin-only)
      const invitedMembers =
        isCurrentUserAdmin || isCurrentUserOwner
          ? (workspace.pending_members ?? []).filter(
              (memb) => memb.member_status === 'invited pending acceptance',
            )
          : [];
      return [...activeMembers, ...invitedMembers];
    }
    if (filter === FILTERS.REQUESTED) {
      return workspace?.pending_members?.filter(
        (memb) => memb.member_status === 'requested to join',
      );
    }
    return workspace?.members?.filter(
      (memb) => memb?.license_status === 'trial',
    );
  }, [
    workspace,
    filter,
    isCurrentUserAdmin,
    isCurrentUserOwner,
  ]) as GetWorkspaceResponse['workspace']['members'];

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <CircularSpinnerß
          size="lg"
          data-testid="team-profile-loading-spinner"
        />
      </div>
    );
  }

  if (!isAccessLoading && !hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <p className="text-muted text-center">{t('noAccessMessage')}</p>
      </div>
    );
  }

  if (!hasWorkspace) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <h2 className="text-xl font-semibold mb-2">
          {tCommon('accessDenied.title')}
        </h2>
        <p className="text-muted text-center">
          {tCommon('accessDenied.message')}
        </p>
      </div>
    );
  }
  return (
    <ContentContainer width="full">
      <div>
        {(showRequestedTab || showTrialsTab) && (
          <fieldset className="mb-2">
            <legend className="sr-only">{t('filters.filterMembers')}</legend>
            <div className="flex gap-2">
              <label className="cursor-pointer">
                <input
                  type="radio"
                  name="member-filter"
                  value={FILTERS.MEMBERS}
                  checked={filter === FILTERS.MEMBERS}
                  onChange={() => setFilter(FILTERS.MEMBERS)}
                  className="sr-only"
                />
                <Tagß
                  size="md"
                  color={filter === FILTERS.MEMBERS ? 'blue' : 'gray'}
                >
                  {t('filters.members')}
                </Tagß>
              </label>
              {showRequestedTab && (
                <label className="cursor-pointer relative">
                  <input
                    type="radio"
                    name="member-filter"
                    value={FILTERS.REQUESTED}
                    checked={filter === FILTERS.REQUESTED}
                    onChange={() => setFilter(FILTERS.REQUESTED)}
                    className="sr-only"
                  />
                  <Tagß
                    size="md"
                    color={filter === FILTERS.REQUESTED ? 'blue' : 'gray'}
                  >
                    {t('filters.requested')}
                  </Tagß>
                  {requestedCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-negative px-1 text-[10px] font-semibold text-on-negative">
                      {requestedCount}
                    </span>
                  )}
                </label>
              )}
              {showTrialsTab && (
                <label className="cursor-pointer relative">
                  <input
                    type="radio"
                    name="member-filter"
                    value={FILTERS.TRIALS}
                    checked={filter === FILTERS.TRIALS}
                    onChange={() => setFilter(FILTERS.TRIALS)}
                    className="sr-only"
                  />
                  <Tagß
                    size="md"
                    color={filter === FILTERS.TRIALS ? 'blue' : 'gray'}
                  >
                    {t('filters.trials')}
                  </Tagß>
                  {isCurrentUserAdmin && trialsEndingSoonCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-negative px-1 text-[10px] font-semibold text-on-negative">
                      {trialsEndingSoonCount}
                    </span>
                  )}
                </label>
              )}
            </div>
          </fieldset>
        )}
        <div className="min-h-[32px]">
          {enableBulkManagement && (
            <MembersTableHeader
              memberActions={memberActions}
              members={members}
              canHideMembers={canHideMembers}
            />
          )}
        </div>

        <TeamMembersTable
          members={members}
          ownerEmail={ownerEmail}
          isCurrentUserAdmin={isCurrentUserAdmin}
          isCurrentUserOwner={isCurrentUserOwner}
          onNotetakerToggleAction={memberActions.toggleNotetaker}
          memberActions={memberActions}
          enableBulkManagement={enableBulkManagement}
          canHideMembers={canHideMembers}
          viewMode={filter}
          allowAdminVaControl={
            workspace?.settings?.allow_admin_va_control ?? false
          }
          isBasicWorkspace={isBasicWorkspace}
          canAccessAnalytics={canAccessAnalytics}
        />
      </div>
    </ContentContainer>
  );
}
